import { useEffect, useRef, useState } from "react"

import { PanelLeft } from "lucide-react"
import { toast } from "sonner"

import { Header } from "@/components/Header"
import { ChatThread } from "@/components/chat/ChatThread"
import { Composer } from "@/components/chat/Composer"
import { MeetingRangePicker } from "@/components/chat/MeetingRangePicker"
import { PreviewPanel } from "@/components/preview/PreviewPanel"
import { ConversationSidebar } from "@/components/sidebar/ConversationSidebar"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { useAgents } from "@/hooks/useAgents"
import { useChatStream } from "@/hooks/useChatStream"
import { useConversations } from "@/hooks/useConversations"
import { useProjectStarters } from "@/hooks/useProjectStarters"
import { useProjects } from "@/hooks/useProjects"
import { useModels } from "@/hooks/useModels"
import { useRetrievalEngine } from "@/hooks/useRetrievalEngine"
import { useSearchScopes } from "@/hooks/useSearchScopes"
import { useWorkIqPreference } from "@/hooks/useWorkIqPreference"
import {
  assignConversationToProject,
  createDummyChats as createDummyChatsApi,
  MEETING_AGENT_ID,
  WORKIQ_AGENT_ID,
} from "@/lib/api"
import { PreviewProvider } from "@/providers/PreviewProvider"
import type { Agent, FileAttachment, MeetingContext, MeetingRange } from "@/types/api"

const SIDEBAR_STORAGE_KEY = "sidebar-width"
const SIDEBAR_MIN = 220
const SIDEBAR_MAX = 520
const SIDEBAR_DEFAULT = 256

const clampSidebarWidth = (w: number) => Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(w)))

function loadSidebarWidth(): number {
  try {
    const stored = Number(localStorage.getItem(SIDEBAR_STORAGE_KEY))
    if (Number.isFinite(stored) && stored > 0) return clampSidebarWidth(stored)
  } catch {
    // storage unavailable
  }
  return SIDEBAR_DEFAULT
}

export function AppShell() {
  const { conversations, loading, refresh, remove } = useConversations()
  const projectsState = useProjects()
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const { agents, loaded: agentsLoaded } = useAgents()
  const { models, selectedId: selectedModelId, selectModel, effortLevels, effort, selectEffort } = useModels()
  const { engines, selectedId: selectedEngineId, selectEngine } = useRetrievalEngine()
  const { scopes, toggleScope } = useSearchScopes()
  const chat = useChatStream({ onConversationCreated: refresh, onTurnDone: () => void projectsState.refresh() })
  const { preferWorkIq, remember } = useWorkIqPreference()
  // Start in the remembered mode with a placeholder so Classic never flashes while the agent list loads.
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(() =>
    preferWorkIq ? { id: WORKIQ_AGENT_ID, label: "Work IQ", description: "" } : null,
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  const workIqAgent = agents.find((a) => a.id === WORKIQ_AGENT_ID) ?? null

  // Reconcile the placeholder once the agent list has loaded (fresh session only).
  const [restored, setRestored] = useState(false)
  useEffect(() => {
    if (restored || !agentsLoaded) return
    setRestored(true)
    if (chat.conversationId) return
    setSelectedAgent((current) => (current?.id === WORKIQ_AGENT_ID ? workIqAgent : current))
  }, [restored, agentsLoaded, workIqAgent, chat.conversationId])

  // User-driven agent changes (mode switch, slash commands) update the remembered mode.
  const selectAgent = (agent: Agent | null) => {
    setSelectedAgent(agent)
    remember(agent?.id === WORKIQ_AGENT_ID)
  }

  const selectConversation = (id: string) => {
    setSheetOpen(false)
    setActiveProjectId(conversations.find((conversation) => conversation.id === id)?.project_id ?? null)
    if (id !== chat.conversationId) {
      void chat.loadConversation(id).then((agentId) => {
        // Restore the agent that was resolved for this conversation's last turn,
        // or null if none (unscoped conversation).
        if (agentId) {
          const agent = agents.find((a) => a.id === agentId)
          setSelectedAgent(agent ?? null)
        } else {
          setSelectedAgent(null)
        }
      })
    }
  }

  const deleteConversation = async (id: string) => {
    const wasActive = chat.conversationId === id
    await remove(id)
    if (wasActive) chat.reset()
  }

  const newConversation = (projectId: string | null = null) => {
    setSheetOpen(false)
    setActiveProjectId(projectId)
    // Fresh conversation: stay in Work IQ if it is active right now, otherwise Classic
    setSelectedAgent(selectedAgent?.id === WORKIQ_AGENT_ID ? selectedAgent : null)
    chat.reset()
  }

  const createDummyChats = async () => {
    try {
      await createDummyChatsApi(3)
      await Promise.all([refresh(), projectsState.refresh()])
    } catch {
      toast.error("Dummy chats could not be created.")
    }
  }

  const moveConversation = async (conversationId: string, projectId: string | null) => {
    try {
      await assignConversationToProject(conversationId, projectId)
      await Promise.all([refresh(), projectsState.refresh()])
      if (conversationId === chat.conversationId) setActiveProjectId(projectId)
    } catch {
      toast.error("Conversation could not be moved. Please try again.")
    }
  }

  const sendMessage = (
    text: string,
    options: {
      attachment: FileAttachment | null
      meetingRange?: MeetingRange | null
      meeting?: MeetingContext | null
    } = { attachment: null },
  ) => {
    void chat.send(text, {
      attachment: options.attachment,
      agentId: selectedAgent?.id ?? null,
      modelId: selectedModelId,
      effort,
      retrievalEngine: selectedEngineId,
      scopes,
      projectId: activeProjectId,
      meetingRange: options.meetingRange ?? null,
      meeting: options.meeting ?? null,
    })
  }

  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth)
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null)

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarWidth))
    } catch {
      // storage unavailable; width just won't persist
    }
  }, [sidebarWidth])

  const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { startX: e.clientX, startWidth: sidebarWidth }
    document.body.style.userSelect = "none"
  }
  const onResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    setSidebarWidth(clampSidebarWidth(drag.startWidth + e.clientX - drag.startX))
  }
  const endResize = () => {
    dragRef.current = null
    document.body.style.userSelect = ""
  }
  const onResizeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") setSidebarWidth((w) => clampSidebarWidth(w - 16))
    else if (e.key === "ArrowRight") setSidebarWidth((w) => clampSidebarWidth(w + 16))
    else return
    e.preventDefault()
  }

  const activeProject = projectsState.projects.find((project) => project.id === activeProjectId) ?? null
  const starters = useProjectStarters(activeProject?.id ?? null, activeProject?.reference_count ?? 0, selectedModelId)

  const sidebarProps = {
    conversations,
    activeId: chat.conversationId,
    loading,
    onSelect: selectConversation,
    onDelete: (id: string) => void deleteConversation(id),
    onNew: () => newConversation(),
    projects: projectsState.projects,
    projectsLoading: projectsState.loading,
    activeProjectId,
    onCreateProject: projectsState.create,
    onUpdateProject: projectsState.update,
    onDeleteProject: async (id: string, disposition: "move_to_regular" | "delete_chats") => {
      await projectsState.remove(id, disposition)
      await refresh()
      if (activeProjectId === id) newConversation()
    },
    onNewProjectChat: (id: string) => newConversation(id),
    onReferencesChanged: () => void projectsState.refresh(),
    onMoveConversation: (conversationId: string, projectId: string | null) => moveConversation(conversationId, projectId),
    onCreateDummyChats: () => void createDummyChats(),
    workIqAvailable: workIqAgent !== null,
    workIq: selectedAgent?.id === WORKIQ_AGENT_ID,
    onWorkIqChange: (workIq: boolean) => selectAgent(workIq ? workIqAgent : null),
  }

  return (
    <PreviewProvider>
      <div className="flex h-screen flex-col">
        <Header
          menuSlot={
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open conversations">
                  <PanelLeft className="size-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 border-sidebar-border bg-sidebar p-0">
                <SheetTitle className="sr-only">Conversations</SheetTitle>
                <ConversationSidebar {...sidebarProps} />
              </SheetContent>
            </Sheet>
          }
        />

        <div className="flex min-h-0 flex-1">
          <aside className="relative hidden shrink-0 lg:block" style={{ width: sidebarWidth }}>
            <ConversationSidebar {...sidebarProps} />
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize conversation list"
              aria-valuemin={SIDEBAR_MIN}
              aria-valuemax={SIDEBAR_MAX}
              aria-valuenow={sidebarWidth}
              tabIndex={0}
              onPointerDown={startResize}
              onPointerMove={onResizeMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
              onKeyDown={onResizeKey}
              onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
              className="absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize touch-none transition-colors hover:bg-sidebar-ring/40 focus-visible:bg-sidebar-ring/60 focus-visible:outline-none active:bg-sidebar-ring/60"
            />
          </aside>

          <main className="flex min-w-0 flex-1 flex-col bg-background">
            <ChatThread
              messages={chat.messages}
              onSuggestionSelect={sendMessage}
              projectStarters={activeProject ? { projectName: activeProject.name, ...starters } : null}
              projectChoice={{ projects: projectsState.projects, selectedId: activeProjectId, onSelect: setActiveProjectId }}
            />
            {selectedAgent?.id === MEETING_AGENT_ID && (
              <div className="mx-auto w-full max-w-3xl px-6 pb-2">
                <MeetingRangePicker
                  disabled={chat.isStreaming}
                  onConfirm={(range) => sendMessage("Meetings in diesem Zeitraum", { attachment: null, meetingRange: range })}
                />
              </div>
            )}
            <Composer
              agents={agents}
              selectedAgent={selectedAgent}
              onSelectAgent={selectAgent}
              models={models}
              selectedModelId={selectedModelId}
              onSelectModel={selectModel}
              effortLevels={effortLevels}
              effort={effort}
              onSelectEffort={selectEffort}
              retrievalEngines={engines}
              selectedEngineId={selectedEngineId}
              onSelectEngine={selectEngine}
              scopes={scopes}
              onToggleScope={toggleScope}
              isStreaming={chat.isStreaming}
              onSend={sendMessage}
              onPickMeeting={(meeting, text) => sendMessage(text, { attachment: null, meeting })}
              onStop={chat.stop}
            />
          </main>

          <PreviewPanel />
        </div>
      </div>
    </PreviewProvider>
  )
}
